// Shared labels and helpers for the International Orders workflow
// (IntlOrders.js, IntlOrderForm.js, IntlOrderDetail.js). The rules
// themselves live on the server (server/intlOrders/workflow.js). The
// can* helpers here only decide which buttons to show; the server checks
// every action again.

export const STATUS_META = {
  DRAFT: { label: 'Draft', tone: 'neutral' },
  LOGISTICS_PENDING: { label: 'Logistics Pending', tone: 'warning' },
  LOGISTICS_ACCEPTED: { label: 'Logistics Accepted', tone: 'warning' },
  ADDRESS_VERIFIED: { label: 'Address Verified', tone: 'warning' },
  PAYMENT_PENDING: { label: 'Payment Pending', tone: 'primary' },
  PAYMENT_CONFIRMED: { label: 'Payment Confirmed', tone: 'success' },
  PACKING: { label: 'Packing', tone: 'primary' },
  READY_FOR_DISPATCH: { label: 'Ready for Dispatch', tone: 'primary' },
  SHIPPED: { label: 'Shipped', tone: 'success' },
  DELIVERED: { label: 'Delivered', tone: 'success' },
  CANCELLED: { label: 'Cancelled', tone: 'danger' },
};

// Stat cards on the list page: one per team's queue rather than one per
// status, so each team has a single place to look.
export const QUEUES = [
  // Every order still in progress: everything except drafts and finished
  // (delivered or cancelled) orders.
  {
    key: 'running',
    label: 'Running',
    statuses: ['LOGISTICS_PENDING', 'LOGISTICS_ACCEPTED', 'ADDRESS_VERIFIED', 'PAYMENT_PENDING',
      'PAYMENT_CONFIRMED', 'PACKING', 'READY_FOR_DISPATCH', 'SHIPPED'],
    tone: 'primary',
  },
  { key: 'draft', label: 'Drafts', statuses: ['DRAFT'], tone: 'neutral' },
  // ADDRESS_VERIFIED is split on the server (STATUS_SPLITS in store.js):
  // once the courier charge is saved, the order waits on the payment link,
  // so it moves from Courier Charge to Payment Pending.
  { key: 'logistics', label: 'Courier Charge', statuses: ['LOGISTICS_PENDING', 'LOGISTICS_ACCEPTED', 'ADDRESS_VERIFIED:no_charge'], tone: 'warning' },
  { key: 'payment', label: 'Payment Pending', statuses: ['ADDRESS_VERIFIED:charged', 'PAYMENT_PENDING'], tone: 'primary' },
  // Part of Payment Pending: link generated PAYMENT_STALE_HOURS ago or more
  // and still unpaid, so the courier charge should be re-checked.
  { key: 'stale', label: 'Unpaid 2+ days', statuses: ['PAYMENT_PENDING:stale'], tone: 'danger' },
  { key: 'packing', label: 'Packing', statuses: ['PAYMENT_CONFIRMED', 'PACKING'], tone: 'primary' },
  { key: 'dispatch', label: 'Ready for Dispatch', statuses: ['READY_FOR_DISPATCH'], tone: 'primary' },
  { key: 'shipped', label: 'Shipped', statuses: ['SHIPPED'], tone: 'success' },
  { key: 'delivered', label: 'Delivered', statuses: ['DELIVERED'], tone: 'success' },
  { key: 'cancelled', label: 'Cancelled', statuses: ['CANCELLED'], tone: 'danger' },
];

// A queue's count from GET /summary. Split keys ('ADDRESS_VERIFIED:charged')
// are counted in `bySplit`, plain statuses in `byStatus`.
export const queueCount = (queue, summary) =>
  queue.statuses.reduce((sum, key) => sum + (summary?.byStatus?.[key] || summary?.bySplit?.[key] || 0), 0);

// The progress bar on the order page. `index` is the step the order is
// on; earlier steps are done.
export const PROGRESS_STEPS = ['Order', 'Logistics', 'Address', 'Payment', 'Packing', 'Dispatch', 'Delivered'];
const PROGRESS_INDEX = {
  DRAFT: 0,
  LOGISTICS_PENDING: 1,
  LOGISTICS_ACCEPTED: 2,
  ADDRESS_VERIFIED: 3,
  PAYMENT_PENDING: 3,
  PAYMENT_CONFIRMED: 4,
  PACKING: 4,
  READY_FOR_DISPATCH: 5,
  SHIPPED: 6,
  DELIVERED: 7,
};
export const progressIndex = (status) => PROGRESS_INDEX[status] ?? -1;

export const PAYMENT_META = {
  unpaid: { label: 'Not requested', tone: 'neutral' },
  pending: { label: 'Payment Pending', tone: 'warning' },
  partially_paid: { label: 'Partially Paid', tone: 'warning' },
  paid: { label: 'Payment Received', tone: 'success' },
};

export const ADDRESS_META = {
  pending: { label: 'Not verified', tone: 'neutral' },
  verified: { label: 'Address Verified', tone: 'success' },
};

export function StatusPill({ meta }) {
  if (!meta) return null;
  return <span className={`intl-pill intl-pill-${meta.tone}`}>{meta.label}</span>;
}

export const PERMS = {
  CREATE: 'international',
  LOGISTICS: 'international_logistics',
  PACKING: 'international_packing',
  CANCEL: 'international_cancel',
  ADMIN: 'international_admin',
};

// Asks for the delete confirmation. A paid order (superadmin only) needs its
// order number typed; returns the DELETE body, or null if the user backed out.
export const confirmOrderDelete = (order) => {
  if (!['paid', 'partially_paid'].includes(order.payment_status)) {
    return window.confirm(`Delete ${order.order_number} (${order.customer_name})? This can't be undone. Any unpaid payment link is cancelled too.`) ? {} : null;
  }
  const typed = window.prompt(
    `${order.order_number} is PAID. Deleting it also removes its Backend Payments row, and can't be undone (a snapshot stays in the Audit Log). Only do this for a test order.\n\nType ${order.order_number} to delete it:`
  );
  if (typed === null) return null;
  if (typed.trim() !== order.order_number) {
    window.alert(`That doesn't match ${order.order_number}. Nothing was deleted.`);
    return null;
  }
  return { confirm_order_number: order.order_number };
};

export const userCaps = (user) => {
  const perms = new Set(user?.permissions || []);
  const isSuper = user?.role === 'superadmin';
  const admin = isSuper || perms.has(PERMS.ADMIN);
  return {
    create: admin || perms.has(PERMS.CREATE),
    logistics: admin || perms.has(PERMS.LOGISTICS),
    packing: admin || perms.has(PERMS.PACKING),
    cancel: admin || perms.has(PERMS.CANCEL),
    admin,
    // Only a superadmin can delete a paid order (e.g. a paid test order).
    superadmin: isSuper,
  };
};

// The queue a user lands on: a pure logistics or packing user sees their
// own work first instead of every order.
export const defaultQueueFor = (caps) => {
  if (caps.create || caps.admin) return '';
  if (caps.logistics) return 'logistics';
  if (caps.packing) return 'packing';
  return '';
};

// Logistics saves the real courier cost; the customer pays it plus this
// markup (1000 -> 1200). Same rule as customerShippingPaise in
// server/intlOrders/workflow.js, which is what the payment link uses.
export const COURIER_MARKUP_PERCENT = 20;
export const customerShippingPaise = (chargePaise) =>
  (chargePaise === null || chargePaise === undefined
    ? chargePaise
    : Math.round((chargePaise * (100 + COURIER_MARKUP_PERCENT)) / 100));

// Courier rates change, so a link the customer hasn't paid for this long
// should be re-quoted. Same rule as isPaymentStale in
// server/intlOrders/workflow.js.
export const PAYMENT_STALE_HOURS = 48;

// Whole days since the payment link was generated, when the order is
// unpaid past PAYMENT_STALE_HOURS (a partial payment doesn't count); else 0.
export const staleUnpaidDays = (order, now = Date.now()) => {
  if (order.status !== 'PAYMENT_PENDING' || !['pending', 'unpaid'].includes(order.payment_status)) return 0;
  const createdAt = Date.parse(order.payment_link_created_at || '');
  if (Number.isNaN(createdAt)) return 0;
  const hours = (now - createdAt) / 3600000;
  return hours >= PAYMENT_STALE_HOURS ? Math.floor(hours / 24) : 0;
};

// What has to happen next, who does it, and (`action`) the short label
// shown under the status in the list.
// erpPacking: true/false on the order page (packing done in the ERP or the
// CRM); left out on the list, which then uses wording true for both.
export const nextStep = (order, { erpPacking } = {}) => {
  switch (order.status) {
    case 'DRAFT':
      return { tone: 'neutral', who: 'Order team', action: 'Send to logistics', text: 'Complete the customer details and products, then send to logistics.' };
    case 'LOGISTICS_PENDING':
      return { tone: 'warning', who: 'Logistics', action: 'Accept order', text: 'Action required: accept the order and verify the shipping address.' };
    case 'LOGISTICS_ACCEPTED':
      return { tone: 'warning', who: 'Logistics', action: 'Verify address', text: 'Check the shipping address below. Correct it if needed, then verify it.' };
    case 'ADDRESS_VERIFIED':
      if (order.courier_recheck_requested_at) {
        return { tone: 'danger', who: 'Logistics', action: 'Re-check courier charge', text: "The customer didn't pay the last link and courier rates may have changed. Check today's courier charge and save it (even if it's the same). The order team then generates a new payment link." };
      }
      if (order.shipping_charge_paise === null || order.shipping_charge_paise === undefined) {
        return { tone: 'warning', who: 'Logistics', action: 'Add courier charge', text: 'Add the courier charge and save it. The order team then generates the payment link.' };
      }
      return { tone: 'warning', who: 'Order team', action: 'Generate payment link', text: 'Courier charge added. Check the products (add any extra item), then generate the payment link.' };
    case 'PAYMENT_PENDING':
      if (order.payment_status === 'paid') {
        return { tone: 'warning', who: 'Admin', action: 'Check payment', text: "Razorpay shows a payment that doesn't match the order total. Check the activity log." };
      }
      if (staleUnpaidDays(order)) {
        const days = staleUnpaidDays(order);
        return { tone: 'danger', who: 'Order team', action: `Unpaid ${days} days · re-check courier`, text: `The customer hasn't paid in ${days} days since the payment link was generated. Courier rates may have changed: ask logistics to re-check the courier charge before the customer pays.` };
      }
      if (order.payment_status === 'unpaid') {
        return { tone: 'warning', who: 'Order team', action: 'New payment link', text: 'The payment link expired or was cancelled. Generate a new one and send it.' };
      }
      return { tone: 'primary', who: 'Customer', action: 'Awaiting payment', text: 'Waiting for the customer to pay. Razorpay is checked every 2 minutes.' };
    case 'PAYMENT_CONFIRMED':
      if (erpPacking === true) return { tone: 'success', who: 'Packing (ERP)', action: 'Pack in the ERP', text: 'Payment confirmed. The order is on the ERP\'s International packing page.' };
      if (erpPacking === false) return { tone: 'success', who: 'Packing', action: 'Start packing', text: 'Payment confirmed. Start packing the products.' };
      return { tone: 'success', who: 'Packing', action: 'Pack order', text: 'Payment confirmed. Pack the products.' };
    case 'PACKING':
      if (erpPacking === true) return { tone: 'primary', who: 'Packing (ERP)', action: 'Pack in the ERP', text: 'Being packed on the ERP\'s International packing page.' };
      if (erpPacking === false) return { tone: 'primary', who: 'Packing', action: 'Mark as packed', text: 'Tick each product as it is packed, then mark the order packed.' };
      return { tone: 'primary', who: 'Packing', action: 'Being packed', text: 'Being packed.' };
    case 'READY_FOR_DISPATCH':
      return { tone: 'primary', who: 'Logistics', action: 'Dispatch', text: 'Packed. Dispatch it with the courier and AWB number.' };
    case 'SHIPPED':
      return { tone: 'success', who: 'Logistics', action: 'In transit', text: 'Shipped. Mark it delivered once the courier confirms delivery.' };
    case 'DELIVERED':
      return { tone: 'success', who: null, action: null, text: 'Delivered. Nothing left to do.' };
    case 'CANCELLED':
      return { tone: 'danger', who: null, action: null, text: `Cancelled${order.cancel_reason ? `: ${order.cancel_reason}` : '.'}` };
    default:
      return { tone: 'neutral', who: null, action: null, text: '' };
  }
};

export const formatMoney = (paise, currency = 'INR') => {
  if (paise === null || paise === undefined) return '-';
  const value = (Number(paise) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency === 'INR' ? `₹${value}` : `${value} ${currency}`;
};

export const formatQty = (qty) => String(Number(qty));

// The ERP's product unit defaults to 'meters' and was never set per product,
// so 'meters' is believed only when the product name says it's sold by the meter.
// shortcut: name-based guess, drop it once the ERP has the right unit on each product.
const SOLD_BY_METER = /\b(meters?|metres?|mtrs?)\b/i;
export const qtyUnit = (item) => {
  const unit = item?.unit;
  if (!unit || unit === 'pieces') return 'pcs';
  if (unit === 'meters') return SOLD_BY_METER.test(item.name || '') ? 'meters' : 'pcs';
  return unit;
};

export const formatDateTime = (raw) => {
  if (!raw) return '-';
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export const formatDate = (raw) => {
  if (!raw) return '-';
  const parsed = new Date(raw.length === 10 ? `${raw}T00:00:00` : raw);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Reads the JSON body and throws the server's own message on failure, so
// staff see "Add the courier charge before continuing." rather than a
// status code.
export const readJson = async (response, fallback) => {
  const body = await response.json().catch(() => null);
  if (!response.ok) throw new Error(body?.error || fallback);
  return body;
};

const FIELD_LABELS = {
  customer_name: 'Name', customer_number: 'WhatsApp', email: 'Email', country: 'Country', country_code: 'Country code',
  state: 'State', city: 'City', address_line1: 'Address', address_line2: 'Address line 2', postal_code: 'Postal code',
  customer_notes: 'Customer notes', courier_service: 'Courier service', package_weight_grams: 'Weight (g)',
  package_length_cm: 'Length (cm)', package_width_cm: 'Width (cm)', package_height_cm: 'Height (cm)',
  logistics_notes: 'Logistics notes', courier_name: 'Courier', awb_number: 'AWB', tracking_url: 'Tracking URL',
  shipment_date: 'Dispatch date', notes: 'Notes',
};

const describeChanges = (changes) =>
  Object.entries(changes || {})
    .map(([field, { from, to }]) => `${FIELD_LABELS[field] || field}: ${from ?? '—'} → ${to ?? '—'}`)
    .join('; ');

const itemList = (items) => (items || []).map((item) => `${formatQty(item.qty ?? item.to)} x ${item.name}`).join(', ');

const WA_EVENT_LABELS = {
  INTERNATIONAL_ORDER_CREATED: 'order created message',
  INTERNATIONAL_SHIPPING_CHARGE_ADDED: 'shipping charge message',
  INTERNATIONAL_SHIPPING_CHARGE_UPDATED: 'updated amount message',
  INTERNATIONAL_STAFF_NEW_ORDER: 'new order alert to logistics',
  INTERNATIONAL_PAYMENT_LINK_SENT: 'payment link message',
};

// One readable line per history event (see logEvent calls in
// server/intlOrders/service.js).
export const describeEvent = (event, currency = 'INR') => {
  const d = event.details || {};
  switch (event.action) {
    case 'created': return { title: 'Order created', detail: itemList(d.items) };
    case 'sent_to_logistics': return { title: 'Sent to logistics' };
    case 'customer_updated': return { title: 'Customer details changed', detail: describeChanges(d.changes) };
    case 'address_changed': return { title: 'Address changed', detail: describeChanges(d.changes) };
    case 'product_added': return { title: 'Product added', detail: itemList(d.items) };
    case 'product_removed': return { title: 'Product removed', detail: itemList(d.items) };
    case 'quantity_changed':
      return { title: 'Quantity changed', detail: (d.items || []).map((i) => `${i.name}: ${formatQty(i.from)} → ${formatQty(i.to)}`).join('; ') };
    case 'discount_changed':
      return {
        title: d.to > 0 ? 'Discount set' : 'Discount removed',
        detail: [
          `${formatMoney(d.from, currency)} → ${formatMoney(d.to, currency)}`,
          d.final_total_paise !== null && d.final_total_paise !== undefined ? `final total ${formatMoney(d.final_total_paise, currency)}` : null,
          d.reason,
        ].filter(Boolean).join(' · '),
      };
    case 'extra_item_changed':
      return {
        title: 'Extra item changed',
        detail: (d.items || []).map((i) => `${i.from_name !== i.name ? `${i.from_name} → ` : ''}${i.name}: ${formatMoney(i.from_paise, currency)} → ${formatMoney(i.to_paise, currency)}`).join('; '),
      };
    case 'accepted': return { title: 'Order accepted by logistics' };
    case 'address_verified': return { title: 'Address verified', detail: d.address };
    case 'address_correction_required': return { title: 'Address correction required', detail: d.note };
    case 'address_verification_reset': return { title: 'Address needs re-verification', detail: d.reason };
    case 'courier_charge_added': return { title: 'Courier charge added', detail: formatMoney(d.to, currency) };
    case 'courier_charge_changed':
      return { title: 'Courier charge changed', detail: `${formatMoney(d.from, currency)} → ${formatMoney(d.to, currency)}` };
    case 'logistics_updated': return { title: 'Logistics updated', detail: describeChanges(d.changes) };
    case 'sent_for_payment': return { title: 'Sent for payment' };
    case 'returned_to_logistics':
      return d.recheck_courier_charge
        ? {
          title: 'Sent back to logistics to re-check the courier charge',
          detail: [d.previous_charge_paise != null ? `was ${formatMoney(d.previous_charge_paise, currency)}` : null, d.reason].filter(Boolean).join(' · '),
        }
        : { title: 'Sent back to logistics', detail: d.reason };
    case 'courier_charge_reconfirmed': return { title: 'Courier charge re-checked, unchanged', detail: formatMoney(d.amount_paise, currency) };
    case 'payment_link_created': return { title: 'Payment link generated', detail: `${formatMoney(d.amount_paise, d.currency)} · ${d.short_url}` };
    case 'payment_link_cancelled': return { title: 'Payment link cancelled', detail: d.payment_link_id };
    case 'payment_link_cancel_failed':
      return { title: 'Old payment link could not be cancelled', detail: `${d.payment_link_id}: cancel it in Razorpay. ${d.error || ''}` };
    case 'payment_link_expired': return { title: `Payment link ${d.status || 'expired'}` };
    case 'payment_partially_received': return { title: 'Partial payment received', detail: formatMoney(d.amount_paid_paise, d.currency) };
    case 'payment_received':
      return d.via === 'backend'
        ? { title: 'Payment received (backend)', detail: `${formatMoney(d.amount_paid_paise, d.currency)} · ${d.reference || ''}` }
        : { title: 'Payment received', detail: `${formatMoney(d.amount_paid_paise, d.currency)} · ${d.payment_id || ''}` };
    case 'payment_mismatch':
      return { title: 'Payment does not match order total', detail: `Expected ${formatMoney(d.expected_paise, d.expected_currency)}, received ${formatMoney(d.paid_paise, d.paid_currency)}` };
    case 'payment_confirmed':
    case 'confirmed': // first-release event name
      return {
        title: 'Payment confirmed',
        detail: ['razorpay', 'payment'].includes(d.via)
          ? 'Automatically, from Razorpay'
          : d.via === 'admin_override' ? `By admin without verified payment: ${d.reason}`
            : d.via === 'backend' ? 'Payment received in the backend, marked by an admin' : null,
      };
    case 'packing_started': return { title: 'Packing started' };
    case 'packed': return { title: 'Order packed', detail: itemList(d.items) };
    case 'dispatched':
    case 'shipped': // first-release event name
      return { title: 'Order dispatched', detail: `${d.courier_name} · AWB ${d.awb_number}${d.tracking_url ? ' · tracking added' : ''}` };
    case 'tracking_added': return { title: 'Tracking added', detail: describeChanges(d.changes) };
    case 'tracking_changed':
      return {
        title: d.changes?.courier_name || d.changes?.awb_number ? 'Courier / AWB changed' : 'Tracking changed',
        detail: describeChanges(d.changes),
      };
    case 'delivered': return { title: 'Marked delivered' };
    case 'whatsapp_sent': return { title: `WhatsApp sent: ${WA_EVENT_LABELS[d.event_type] || d.event_type}`, detail: d.campaign };
    case 'whatsapp_failed': return { title: `WhatsApp failed: ${WA_EVENT_LABELS[d.event_type] || d.event_type}`, detail: d.error };
    case 'whatsapp_retry': return { title: `WhatsApp retried: ${WA_EVENT_LABELS[d.event_type] || d.event_type}` };
    case 'cancelled':
      return { title: 'Order cancelled', detail: `${d.reason}${d.refund_needed ? ' · Refund needed in Razorpay' : ''}` };
    default: return { title: event.action.replace(/_/g, ' ') };
  }
};
